import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  FormLabel,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  GetReportsByCreditFacilityIdQueryGQLType,
  useGetReportsByCreditFacilityIdQuery,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useDownloadReportUrl } from '../hooks/useDownloadReportUrl';
import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { DownloadIconButton } from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { FiCheck } from 'react-icons/fi';

type ReportGqlType = GetReportsByCreditFacilityIdQueryGQLType['reports'][number];

type ReportRowProps = { report: ReportGqlType };
const ReportRow = ({ report }: ReportRowProps) => {
  const { reportName, reportType, reportingDate, version, isPreview } = report;

  const { getReportDownloadUrl, isLoading } = useDownloadReportUrl(report);

  return (
    <Tr>
      <Td>{reportName}</Td>
      <Td>{commonUtils.toTitleCase(reportType)}</Td>
      <Td textAlign="center">
        {isPreview ? <Icon aria-label="is preview" as={FiCheck} /> : '-'}
      </Td>
      <Td>{reportingDate}</Td>
      <Td>{version}</Td>
      <Td textAlign="center">
        <DownloadIconButton
          ariaLabel="download report"
          label="Download report"
          fontSize="xl"
          position="static"
          isLoading={isLoading}
          onClick={getReportDownloadUrl}
        />
      </Td>
    </Tr>
  );
};

const REPORT_STATES = ['Live', 'Preview'] as const;
type REPORT_STATE_TYPE = typeof REPORT_STATES[number];
const REPORT_OPTIONS = REPORT_STATES.map((state) => ({
  value: state,
  label: state,
}));
const previewLabel =
  'Preview reports are generated for internal review, and are not sent to the capital provider.';

type ServicerReportsTableProps = { creditFacilityId: string };
export const ServicerReportsTable = ({
  creditFacilityId,
}: ServicerReportsTableProps) => {
  const borderColor = useColorModeValue('gray.100', 'gray.900');
  const toast = useToast();
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [reportState, setReportState] = useState<REPORT_STATE_TYPE>('Live');

  // NOTE: this currently fetches all servicer reports for the creditFacility.
  // If this list size becomes very large (still far from this), then we can
  // consider updating this logic to incorporate pagination
  const { data: reportsData } = useGetReportsByCreditFacilityIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: { creditFacilityId },
  });

  const targetReports = useMemo<ReportGqlType[]>(() => {
    if (!reportsData) {
      return [];
    }

    return reportsData.reports.filter((report) => {
      const { reportingDate, isPreview } = report;

      // *** Reporting Date ***
      if (startDate) {
        const startDateStr = commonDate.dateToFullDateString(
          startDate,
          clientTimeZone,
        );
        if (reportingDate.localeCompare(startDateStr) === -1) {
          return false;
        }
      }
      if (endDate) {
        const endDateStr = commonDate.dateToFullDateString(
          endDate,
          clientTimeZone,
        );
        if (endDateStr.localeCompare(reportingDate) === -1) {
          return false;
        }
      }

      // *** Report State ***
      if (reportState === 'Live' && isPreview) {
        return false;
      }
      if (reportState === 'Preview' && !isPreview) {
        return false;
      }

      return true;
    });
  }, [clientTimeZone, endDate, reportState, reportsData, startDate]);

  if (!reportsData) {
    return (
      <Skeleton data-testid="loading-servicer-reports-table" height="600px" />
    );
  }

  if (reportsData.reports.length === 0) {
    return (
      <Alert status="info" mt="5">
        <AlertIcon />
        <AlertDescription>No reports are currently available</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box id="servicer-reports-table-container" minH="300px">
      <Flex gap="1rem" flexWrap="wrap" marginBottom="1rem" width="100%">
        <Flex alignItems="center" mr="8">
          <FormLabel mb="0" mr="2" fontWeight="normal">
            Report State:
          </FormLabel>
          <Box w="32">
            <Select<REPORT_STATE_TYPE>
              value={reportState}
              options={REPORT_OPTIONS}
              onChange={(field) => {
                if (field) {
                  setReportState(field);
                }
              }}
            />
          </Box>
        </Flex>

        <FilterDatePicker
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          label="Date Range:"
          FlexProps={{
            flexShrink: 0,
            minWidth: 'fit-content',
            width: '100%',
          }}
        />
      </Flex>

      {targetReports.length === 0 ? (
        <Alert status="info" mt="5">
          <AlertIcon />
          <AlertDescription>
            No reports per the utilized filters
          </AlertDescription>
        </Alert>
      ) : (
        <Flex
          width="100%"
          borderWidth="1px"
          borderColor={borderColor}
          maxH="60vh"
          overflow="auto"
        >
          <Table>
            <Thead position="sticky" top="0">
              <Tr>
                <Th minWidth="20rem">Name</Th>
                <Th minWidth="15rem">Report Type</Th>
                <Th minWidth="8rem" textAlign="center">
                  Preview{' '}
                  <Tooltip hasArrow label={previewLabel}>
                    <InfoOutlineIcon ml="2px" />
                  </Tooltip>
                </Th>
                <Th minWidth="10rem">Reporting Date</Th>
                <Th minWidth="6rem">Version</Th>
                <Th minWidth="6rem" textAlign="center">
                  Download
                </Th>
              </Tr>
            </Thead>

            <Tbody>
              {targetReports.map((report) => (
                <ReportRow key={report.id} report={report} />
              ))}
            </Tbody>
          </Table>
        </Flex>
      )}
    </Box>
  );
};
